/*
  PatchStitch 定制区：上传占位状态 + 界面文案本地化（所有商品页，由 sections/lf-product-polish.liquid 加载）
  - 上传中：应用只给 .ps-upload__dropzone 加 is-busy、改一行提示。这里把选择按钮和文件框禁用、按钮换成加载文案，
    样式（转圈 + 进度条）在 lf-product-polish.liquid；上传期间离开页面会先弹浏览器确认
  - 裁剪框「Foto verwenden」点一次后锁住：生成图片要一两秒，连点会重复上传
  - 应用的提示、报错有一部分是写死的英文，按原文对上后换成 locales 里 lf_customization.* 的译文
  - 应用每次输入都会重建选项 DOM：用 MutationObserver 在微任务里同步，只在内容不同时才写，避免死循环
*/
(() => {
  if (window.lfCustomizationUploadReady) return;
  const source = document.querySelector('[data-lf-customization-text]');
  if (!source) return;
  window.lfCustomizationUploadReady = true;
  const T = JSON.parse(source.textContent);
  const live = document.querySelector('[data-lf-upload-status]');

  /* 应用报错原文（开头匹配）→ 译文 key */
  const STATUS = [
    [/^Image upload failed/, 'upload_failed'],
    [/^Could not load customization options/, 'options_failed'],
    [/^Loading customization options/, 'loading_options'],
    [/^Your photo is still uploading/, 'still_uploading'],
    [/^Calculating the price/, 'calculating_price'],
    [/^Please fill in all required options/, 'required_options']
  ];

  const setText = (node, text) => { if (node && text && node.textContent !== text) node.textContent = text; };
  const setAttr = (node, name, value) => { if (node && node.getAttribute(name) !== value) node.setAttribute(name, value); };

  let wasBusy = false;
  const announce = text => { if (live && text) { live.textContent = ''; requestAnimationFrame(() => { live.textContent = text; }); } };

  const syncUpload = upload => {
    const zone = upload.querySelector('.ps-upload__dropzone');
    if (!zone) return false;
    const busy = zone.classList.contains('is-busy');
    const button = zone.querySelector('.ps-upload__choose-btn');
    const input = upload.querySelector('.ps-upload__input');
    setAttr(zone, 'aria-busy', String(busy));
    if (button && button.disabled !== busy) button.disabled = busy;
    /* 禁用的文件框 click() 不会弹选择窗，拖放在 CSS 里用 pointer-events 挡住 */
    if (input && input.disabled !== busy) input.disabled = busy;
    setText(button, busy ? T.uploading : T.choose_file);
    setText(zone.querySelector('.ps-upload__hint'), busy ? T.uploading_note : T.drop_hint);

    /* 上传完成后预览图还要从 CDN 下载：下载完之前显示占位 */
    const preview = upload.querySelector('.ps-upload__preview');
    if (preview) {
      setAttr(preview, 'alt', T.uploaded_photo);
      const wrap = preview.closest('.ps-upload__preview-wrap');
      if (wrap && !preview.dataset.lfWatched) {
        preview.dataset.lfWatched = 'true';
        if (!preview.complete) {
          wrap.classList.add('is-loading');
          const done = () => wrap.classList.remove('is-loading');
          preview.addEventListener('load', done, { once: true });
          preview.addEventListener('error', done, { once: true });
        }
      }
    }
    return busy;
  };

  const syncRoot = root => {
    let busy = false;
    root.querySelectorAll('.ps-upload').forEach(upload => { if (syncUpload(upload)) busy = true; });
    setText(root.querySelector('[data-patchstitch-loading]'), T.loading);
    root.querySelectorAll('.ps-option__error-msg').forEach(node => { if (node.textContent.trim() === 'This field is required.') setText(node, T.required); });
    root.querySelectorAll('.ps-option__clear').forEach(node => { if (node.textContent.trim() === 'Clear') setText(node, T.clear); });
    root.querySelectorAll('select > option[value=""]').forEach(node => { if (node.textContent.trim() === 'Select…') setText(node, T.select_placeholder); });
    /* 后台标题里自带「*」的必填项会显示成「Schriftstil * *」：去掉标题文字末尾的星号，只留应用的必填标记 */
    root.querySelectorAll('.ps-option__title').forEach(title => {
      const text = title.firstChild;
      if (text && text.nodeType === Node.TEXT_NODE && title.querySelector('.ps-option__required-mark') && /\s*\*+\s*$/.test(text.data)) {
        text.data = text.data.replace(/\s*\*+\s*$/, '');
      }
    });

    const status = root.querySelector('[data-patchstitch-error]');
    if (status) {
      const match = STATUS.find(([pattern]) => pattern.test(status.textContent.trim()));
      if (match) setText(status, T[match[1]]);
    }

    /* 「Selection will add <金额> to the price.」：只换两段文字，金额节点保持应用自己的 */
    const price = root.querySelector('[data-patchstitch-price] > span');
    if (price && price.firstChild && price.firstChild.nodeType === Node.TEXT_NODE && price.firstChild.data === 'Selection will add ') {
      const [before, after = ''] = T.price_delta.split('[amount]');
      price.firstChild.data = before;
      if (price.lastChild && price.lastChild.nodeType === Node.TEXT_NODE) price.lastChild.data = after;
    }
    return busy;
  };

  const sync = () => {
    let busy = false;
    document.querySelectorAll('[data-patchstitch-root]').forEach(root => { if (syncRoot(root)) busy = true; });
    setText(document.querySelector('.ps-crop-overlay .ps-crop-hint'), T.crop_hint);
    if (busy !== wasBusy) {
      if (busy) announce(T.uploading);
      else announce(document.querySelector('[data-patchstitch-error][data-tone="error"]:not([hidden])') ? T.upload_failed : T.upload_done);
      wasBusy = busy;
    }
  };

  let queued = false;
  const schedule = () => {
    if (queued) return;
    queued = true;
    queueMicrotask(() => { queued = false; sync(); });
  };

  /* 裁剪框「使用照片」：第一次点击照常交给应用，之后的点击在捕获阶段拦下 */
  document.addEventListener('click', event => {
    const done = event.target instanceof Element && event.target.closest('.ps-crop-btn--primary');
    if (!done) return;
    if (done.dataset.lfBusy) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    done.dataset.lfBusy = 'true';
    done.setAttribute('aria-disabled', 'true');
    done.closest('.ps-crop-dialog')?.setAttribute('aria-busy', 'true');
    queueMicrotask(() => setText(done, T.preparing));
  }, true);

  /* 上传进行中离开页面（返回、刷新、点链接）时先让浏览器确认 */
  window.addEventListener('beforeunload', event => {
    if (!document.querySelector('[data-patchstitch-root] .ps-upload__dropzone.is-busy')) return;
    event.preventDefault();
    event.returnValue = '';
  });

  const start = () => {
    sync();
    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['class', 'hidden'] });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
